import { useCallback, useEffect, useState } from 'react';
import { getSignaturesRecues } from '../services/sessionService';
import FiltresRepliables from '../components/FiltresRepliables';
import { formatDate } from './devisUtils';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Listes.css';

const DEMI = { matin: 'Matin', apres_midi: 'Après-midi' };

/** « 3 oct. 2026 à 14:05 » : la date seule ne dit pas qui a signé en premier. */
function formatInstant(valeur) {
  if (!valeur) return '—';
  const d = new Date(valeur);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(valeur)} à ${d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * Menu Clients → Signatures reçues : toutes les signatures posées depuis les
 * liens publics d'émargement, la plus récente en tête. On y vérifie d'un
 * coup d'œil qui a signé, pour quelle séance et pour quel client, tracé
 * compris.
 */
function SignaturesRecues() {
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);

  const [signatures, setSignatures] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setRecherche(saisie), 300);
    return () => clearTimeout(t);
  }, [saisie]);

  useEffect(() => {
    setPage(1);
  }, [du, au, recherche]);

  const charger = useCallback(() => {
    setChargement(true);
    setErreur(null);
    return getSignaturesRecues({ du, au, search: recherche, page, limit: 50 })
      .then((res) => {
        setSignatures(res.data || []);
        setPagination(res.pagination || { page: 1, limit: 50, total: 0 });
      })
      .catch((err) => setErreur(err.message || 'Impossible de charger les signatures.'))
      .finally(() => setChargement(false));
  }, [du, au, recherche, page]);

  useEffect(() => {
    charger();
  }, [charger]);

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));
  const rangeStart = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const rangeEnd = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="devis-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Signatures reçues</h1>
          <p className="devis-subtitle">
            Les signatures posées par les clients et les formateurs depuis les liens publics
            d'émargement, la plus récente en premier.
          </p>
        </div>
      </div>

      <FiltresRepliables libelle="Période et recherche">
        <div className="devis-toolbar reporting-toolbar">
          <div className="reporting-periode">
            <label className="reporting-champ">
              <span>Du</span>
              <input type="date" value={du} max={au || undefined} onChange={(e) => setDu(e.target.value)} />
            </label>
            <label className="reporting-champ">
              <span>Au</span>
              <input type="date" value={au} min={du || undefined} onChange={(e) => setAu(e.target.value)} />
            </label>
          </div>
          <input
            type="search"
            className="devis-search"
            placeholder="Rechercher un signataire, une formation, un client, un contrat"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
          />
        </div>
      </FiltresRepliables>

      <div className="devis-card">
        {chargement && <div className="devis-state">Chargement...</div>}
        {!chargement && erreur && <div className="devis-state devis-state-error">{erreur}</div>}
        {!chargement && !erreur && signatures.length === 0 && (
          <div className="devis-state">
            Aucune signature reçue pour l'instant : elles apparaîtront ici dès qu'un participant
            ou un formateur aura signé depuis son lien d'émargement.
          </div>
        )}

        {!chargement && !erreur && signatures.length > 0 && (
          <div className="devis-table-wrap">
          <table className="devis-table">
            <thead>
              <tr>
                <th>SIGNÉ LE</th>
                <th>SIGNATAIRE</th>
                <th>SIGNATURE</th>
                <th>SÉANCE</th>
                <th>CLIENT</th>
              </tr>
            </thead>
            <tbody>
              {signatures.map((s) => (
                <tr key={s.id} className="devis-row">
                  <td data-label="SIGNÉ LE">
                    <div className="devis-contact-name">{formatInstant(s.signeLe)}</div>
                    <div className="devis-status-detail">
                      séance du {formatDate(s.jour)} · {DEMI[s.demiJournee] || s.demiJournee}
                    </div>
                  </td>
                  <td data-label="SIGNATAIRE">
                    <div className="devis-contact-name">{s.signataireNom || '—'}</div>
                    <div className="devis-status-detail">
                      {s.estFormateur ? 'Formateur/trice' : s.signataireQualite || 'Participant'}
                    </div>
                  </td>
                  <td data-label="SIGNATURE">
                    {s.image ? (
                      <img
                        src={s.image}
                        alt={`Signature de ${s.signataireNom || 'la personne'}`}
                        style={{
                          width: 120,
                          height: 44,
                          objectFit: 'contain',
                          background: '#fff',
                          border: '1px solid #e2e2ea',
                          borderRadius: 6,
                        }}
                      />
                    ) : (
                      <span className="devis-status-detail">tracé indisponible</span>
                    )}
                  </td>
                  <td data-label="SÉANCE">
                    <div className="devis-contact-name texte-borne">{s.intitule}</div>
                    <div className="devis-status-detail">
                      {[s.formateurs, `contrat ${s.contratNumero}`].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td data-label="CLIENT">
                    <div className="devis-contact-name">{s.societeNom || '—'}</div>
                    <div className="devis-status-detail">
                      {[s.contact, s.contactEmail].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {!chargement && !erreur && pagination.total > pagination.limit && (
        <div className="devis-pagination">
          <span className="devis-pagination-info">
            {rangeStart}-{rangeEnd} sur {pagination.total}
          </span>
          <div className="devis-pagination-controls">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Précédent
            </button>
            <span className="devis-pagination-page">
              Page {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignaturesRecues;
